import classNames from "classnames";
import { FieldProps } from "formik";
import * as React from "react";

import styles from "./styles.scss";

interface FormikProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "form">,
    FieldProps<string, never> {
  hasError?: boolean;
  fullSize?: boolean;
}
interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
  fullSize?: boolean;
  textareaMaxHeightBaseElementRef?: React.RefObject<HTMLElement | null>;
}

const Textarea: React.FC<Props | FormikProps> = (props) => {
  if ("field" in props) {
    return <FormikTextarea {...props} />;
  } else {
    return <PlainTextarea {...props} />;
  }
};

const FormikTextarea: React.FC<FormikProps> = (props) => {
  const { form, field, hasError, ...attributes } = props;
  const touched = !!form.touched[field.name];
  const error = !!form.errors[field.name];

  return (
    <PlainTextarea
      {...attributes}
      {...field}
      hasError={hasError || (touched && error)}
    />
  );
};

const PlainTextarea: React.FC<Props> = (props) => {
  const {
    hasError,
    fullSize = false,
    textareaMaxHeightBaseElementRef,
    ...attributes
  } = props;
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

  const updateTextareaHeight = React.useCallback(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "0px"; //毎回0にすることでテキスト入力エリアのscrollHightを正しく計算する
      const scrollHeight = textAreaRef.current.scrollHeight;
      textAreaRef.current.style.height = `${scrollHeight}px`;

      // どこまで高さを広げるかを決める
      const baseElement = textareaMaxHeightBaseElementRef?.current;
      if (baseElement) {
        // 基準の要素の高さの50%の高さまで広げて良いものとする
        const baseHeight = baseElement.scrollHeight;
        textAreaRef.current.style.maxHeight = `${baseHeight * 0.5}px`;
      }
    }
  }, [textareaMaxHeightBaseElementRef]);

  // テキストエリアの入力内容が変わったらテキストエリアの高さを更新
  React.useEffect(() => {
    updateTextareaHeight();
  }, [attributes.value, updateTextareaHeight]);

  // テキストエリアの基準となる要素の高さが変わったらテキストエリアの高さを更新
  React.useEffect(() => {
    const baseElement = textareaMaxHeightBaseElementRef?.current;
    if (!baseElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateTextareaHeight();
    });

    resizeObserver.observe(baseElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [textareaMaxHeightBaseElementRef, updateTextareaHeight]);

  return (
    <textarea
      ref={textAreaRef}
      {...attributes}
      className={classNames(props.className, styles.root, {
        [styles.hasError]: hasError,
        [styles.full]: fullSize,
      })}
    />
  );
};

export default Textarea;
